import { Formik, Form } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { useAuth } from "@/core/auth";

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const isMobile = useIsMobile();

  const [showPassword, setShowPassword] = useState(false);

  return (
    <Stack
      gap={4}
      direction="row"
      align="center"
      justify="center"
      className={cn("w-full", "min-h-full overflow-hidden p-6", {
        "overflow-y-auto": isMobile,
        "p-4": isMobile,
      })}
    >
      <Card
        className={cn("w-full max-w-md gap-0 p-6 pt-8", {
          "p-4 pt-5": isMobile,
        })}
      >
        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={loginSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await login(values);
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Couldn't sign in. Please try again.",
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ getFieldProps, isSubmitting, isValid }) => (
            <Form>
              <Stack
                align="start"
                gap={4}
                className={cn("w-full", "gap-y-10", {
                  "gap-y-6": isMobile,
                })}
              >
                <Text
                  variant="title"
                  render={<h1 />}
                  className={cn("mt-4", {
                    "mt-2": isMobile,
                  })}
                >
                  Sign in to your account
                </Text>

                <Stack
                  align="start"
                  gap={isMobile ? 4 : 6}
                  className="w-full pb-4"
                >
                  <FormInput
                    id="email"
                    type="email"
                    label="Email"
                    placeholder="Enter your email"
                    {...getFieldProps("email")}
                    required
                    disableRequirementAsterisk
                    fullWidth
                  />

                  <FormInput
                    id="password"
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    placeholder="Enter your password"
                    {...getFieldProps("password")}
                    fullWidth
                    required
                    disableRequirementAsterisk
                    RightActionComponent={
                      <Button
                        type="button"
                        variant="link"
                        size="xs"
                        aria-pressed={showPassword}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </Button>
                    }
                  />
                </Stack>

                <Button
                  type="submit"
                  size="lg"
                  disabled={!isValid}
                  loading={isSubmitting}
                  loadingLabel="Sign in, in progress"
                  className="w-full"
                >
                  Sign in
                </Button>

                <Stack
                  className="w-full"
                  direction={isMobile ? "column" : "row"}
                  gap={2}
                  align={isMobile ? "start" : "center"}
                  justify={isMobile ? "start" : "end"}
                >
                  <Button
                    type="button"
                    variant="link"
                    className={cn("h-auto self-end p-0", {
                      "self-start": isMobile,
                    })}
                    onClick={() => navigate("/signup")}
                  >
                    Sign up
                  </Button>
                  {!isMobile && (
                    <Separator orientation="vertical" className="h-4" />
                  )}
                  <Button
                    type="button"
                    variant="link"
                    className={cn("h-auto self-end p-0", {
                      "self-start": isMobile,
                    })}
                    onClick={() => navigate("/forgot-password")}
                  >
                    Forgot password
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
